const { DOMParser } = require("xmldom");
import {
    AccountingLineVatAssist,
    extractNumberBtqStreet,
    VatItemAssist,
    VatItemAuto,
} from "../../../../backend/src/common/declaration/declarationServices";
import axios from "axios";
import { getUserDisplayName } from "@/services/userServices";
import moment from "moment-timezone";
import { DeclarationFront } from "./DeclarationFront";
import { PreviousVatCredit } from "../../../../backend/src/common/types/taxformTypes";

export function getPaymentsFromXml(xml: string) {
    let paymentsInfo = { paymentsList: null, amountPaid: 0 };
    if (xml) {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xml, "application/xml");
        const list = [];
        let total = 0;
        const allElements = Array.from(
            xmlDoc.getElementsByTagName("*") as HTMLCollectionOf<HTMLElement>,
        );
        const ibanElements = allElements.filter((element) => element.nodeName.startsWith("Iban"));
        const bicElements = allElements.filter((element) => element.nodeName.startsWith("Bic"));
        const amountElements = allElements.filter((element) =>
            element.nodeName.includes("Montant"),
        );
        for (let i = 0; i < ibanElements.length; i++) {
            const payment = {
                iban: ibanElements[i]?.textContent?.trim() || "",
                bic: bicElements[i]?.textContent?.trim() || "",
                amount: amountElements[i]?.textContent?.trim() || "",
            };
            list.push(payment);
            total += parseFloat(payment.amount);
        }
        paymentsInfo = { paymentsList: list, amountPaid: total };
    }
    return paymentsInfo;
}

export function getAccountingLinesFromBankTransaction(bankTransaction): AccountingLineVatAssist[] {
    const als: AccountingLineVatAssist[] = bankTransaction?.accountingLines?.length
        ? bankTransaction.accountingLines.map((al) => ({
              aaName: al.accountableAccount?.name || "",
              thirdPartyCompanyName: al.thirdPartyCompany?.name || "",
              userDisplayName: al.user ? getUserDisplayName(al.user) : "",
          }))
        : [];
    return als;
}

export function isGrossAmountExceededVatAssist(assistVatItems: VatItemAssist[]): boolean {
    if (assistVatItems?.length) {
        for (const item of assistVatItems) {
            if (item.isSubItem) {
                const subItems: VatItemAssist[] = assistVatItems.filter(
                    (v) => v.bankTransactionId === item.bankTransactionId && v.isSubItem,
                );
                if (subItems?.length) {
                    const totalGrossAmount: number = subItems.reduce(
                        (acc, curr) => acc + Number(curr.grossAmount),
                        0,
                    );
                    const grossAmountBeforeSplit: number =
                        assistVatItems.find((x) => x.bankTransactionId === item.bankTransactionId)
                            ?.grossAmountBeforeSplit || 0;
                    if (totalGrossAmount > grossAmountBeforeSplit) {
                        return true;
                    }
                }
            }
        }
    }
    return false;
}

export function getSubItemsVatAssist(vatItems: VatItemAssist[], btId: string): VatItemAssist[] {
    return vatItems.filter((item) => item.bankTransactionId === btId && item.isSubItem);
}

export function deleteSubOrAddedAssistItem(
    vatItems: VatItemAssist[],
    itemToDelete: VatItemAssist,
): VatItemAssist[] {
    vatItems = vatItems.filter((item) => item.subOrAddedItemId !== itemToDelete.subOrAddedItemId);
    const btId: string = itemToDelete.bankTransactionId;
    // check is this is a sub-item
    if (btId) {
        const subItems: VatItemAssist[] = getSubItemsVatAssist(vatItems, btId);
        // return to original state if no more sub-items
        if (!subItems?.length) {
            const sourceItem: VatItemAssist = vatItems.find(
                (item) => item.bankTransactionId === btId && item.hasBeenSplitted,
            );
            if (sourceItem) {
                sourceItem.hasBeenSplitted = false;
                sourceItem.grossAmount = sourceItem.grossAmountBeforeSplit;
                sourceItem.grossAmountBeforeSplit = null;
            }
        }
    }
    return vatItems;
}

export class DasTwoItem {
    // constructor properties
    address: string = "";
    city: string = "";
    postalCode: string = "";
    siret: string = "";

    // directly initialized properties
    postOfficeDistributor: string = "";

    // calculated properties
    number: string = "";
    btq: string = "";
    street: string = "";
    inseeCode: string = "";

    constructor(address: string, city: string, postalCode: string, siret: string) {
        this.address = address;
        this.city = city;
        this.postalCode = postalCode;
        this.siret = siret;

        const numberBtqStreet: { number: string; btq: string; street: string } =
            extractNumberBtqStreet(this.address) || null;
        this.number = numberBtqStreet?.number || "";
        this.btq = numberBtqStreet?.btq || "";
        this.street = numberBtqStreet?.street || "";
    }

    async getCityInseeCode() {
        if (this.postalCode?.length !== 5) {
            this.inseeCode = "";
        } else {
            await axios
                .get(
                    `https://data.opendatasoft.com//api/records/1.0/search/?dataset=correspondance-code-insee-code-postal%40public&q=${this.postalCode}`,
                )
                .then((response) => {
                    // filter by postal code because of two-way correspondence
                    const records: InseeRecord[] = response.data?.records?.filter(
                        (record: InseeRecord) => record.fields.postal_code === this.postalCode,
                    );
                    if (records?.length === 1) {
                        this.inseeCode = records[0].fields.insee_com;
                    } else {
                        // filter by city because of many cities with the same postal code
                        const formattedRecords: FormattedInseeRecord[] = records.map((record) => ({
                            inseeCode: record.fields.insee_com,
                            formattedCity: normalizeCityName(record.fields.nom_comm),
                            postalCode: record.fields.postal_code,
                        }));
                        const formattedCityName: string = normalizeCityName(this.city);
                        const filteredRecords: FormattedInseeRecord[] = formattedRecords.filter(
                            (record) => record.formattedCity === formattedCityName,
                        );
                        if (filteredRecords?.length === 1) {
                            this.inseeCode = filteredRecords[0].inseeCode;
                        }
                    }
                });
        }
    }
}

type InseeRecord = {
    fields: { insee_com: string; nom_comm: string; postal_code: string };
};

type FormattedInseeRecord = {
    inseeCode: string;
    formattedCity: string;
    postalCode: string;
};

function normalizeCityName(name: string) {
    return (
        name
            ?.toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "") // remove accents
            .replace(/-/g, " ") // replaces hyphens with spaces
            .replace(/\s+/g, " ") // remove multiple spaces
            .trim() || ""
    );
}

export class DasTwoBeneficiary extends DasTwoItem {
    id: string = "";
    debit: number = 0;
    accountableAccountName: string = "";
    thirdPartyCompanyName: string = "";
    isExpanded: boolean = true;
    toAdd: boolean = true;
    type: string = "";
    profession: string = "";
    legalForm: string = "";
    naturalPerson: boolean = false;

    constructor(
        address: string = "",
        city: string = "",
        debit: number = 0,
        thirdPartyCompanyName: string = "",
        postalCode: string = "",
        siret: string = "",
        id: string = "",
        accountableAccountName: string = "",
        legalForm: string = "",
    ) {
        super(address, city, postalCode, siret);
        this.id = id;
        this.debit = debit;
        this.thirdPartyCompanyName = thirdPartyCompanyName;
        this.accountableAccountName = accountableAccountName;
        this.legalForm = legalForm;
    }

    toggleExpand() {
        this.isExpanded = !this.isExpanded;
    }

    toggleAdd() {
        this.toAdd = !this.toAdd;
        this.isExpanded = this.toAdd;
    }
}

export class DasTwoDeclaring extends DasTwoItem {
    companyName: string = "";
    closingDate: string = "";
    ape: string = "";
    signatoryName: string = "";
    signatoryEmail: string = "";
    signatoryPhone: string = "";

    constructor(
        address: string = "",
        city: string = "",
        postalCode: string = "",
        siret: string = "",
        companyName: string = "",
        closingDate: string = "",
        ape: string = "",
    ) {
        super(address, city, postalCode, siret);
        this.companyName = companyName;
        this.closingDate = closingDate;
        this.ape = ape;
    }
}

export type Declaring = {
    name: string;
    email: string;
    phone: string;
    cityInseeCode?: string;
    postOfficeDistributor?: string;
    siret?: string;
    legalCategory?: string;
};

export function buildAssistVatItems(
    declarationId: string,
    bankTransactions: any[],
    assistVatItems: VatItemAssist[],
) {
    if (declarationId) {
        // for bankTransactions from store
        for (const bt of bankTransactions) {
            const item = new VatItemAssist(
                bt.bankTransactionId,
                bt.dateDebit,
                bt.wording,
                bt.type,
                bt.grossAmount,
                bt.vatRate,
                bt.netAmount,
                bt.vatAmount,
                bt.toAdd,
                bt.accountingLines,
                bt.isSubItem,
                bt.hasBeenSplitted,
                bt.grossAmountBeforeSplit,
                bt.subOrAddedItemId,
                bt.isAlreadyDeclared,
            );
            assistVatItems.push(item);
        }
    } else {
        // for bankTransactions from Apollo request
        for (const bt of bankTransactions) {
            const item = new VatItemAssist(
                bt.id,
                bt.dateDebit,
                bt.wordingOriginal,
                bt.vatType,
                bt.amount,
                bt.vatRate,
                undefined,
                undefined,
                bt.toAdd,
                getAccountingLinesFromBankTransaction(bt),
                false,
                false,
                null,
                null,
                false,
            );
            assistVatItems.push(item);
        }
    }
}

export function buildAutoVatItems(
    declarationId: string,
    documents: any[],
    autoVatItems: VatItemAuto[],
) {
    if (declarationId) {
        // for documents from store
        for (const document of documents) {
            const vatItem = new VatItemAuto(
                document.docId,
                document.name,
                document.date,
                document.totalGrossAmountSigned,
                document.totalVatToDeclare,
                document.totalNetToDeclare,
                document.toAdd,
                document.details,
                document.grossAmountAls,
                document.vatScheme,
                document.accountingLines,
                document.totalOutputVat,
                document.originalType,
                document.type,
                document.gotAnomalies,
                document.isExpanded,
                document.isAlreadyDeclared,
            );
            autoVatItems.push(vatItem);
        }
    } else {
        // for documents from Apollo request
        for (const document of documents) {
            const vatItem = new VatItemAuto(
                document.id,
                document.name,
                document.date,
                document.vatData.totalGrossAmountSigned,
                document.vatData.toBeDeclared.totalVatAmountSigned,
                document.vatData.toBeDeclared.totalNetAmountSigned,
                document.vatData.toAdd,
                document.vatData.toBeDeclared.details,
                document.vatData.toBeDeclared.grossAmountAls,
                document.vatData.vatScheme,
                document.vatData.toBeDeclared.ptpAls,
                document.vatData.toBeDeclared.totalOutputVat,
                document.originalType,
                "autoValidItems",
                document.vatData.gotAnomalies,
                document.vatData.gotAnomalies ? true : false,
                false,
            );
            autoVatItems.push(vatItem);
        }
    }
    autoVatItems = autoVatItems.filter((item) => item.vatScheme);
    if (autoVatItems.length) {
        autoVatItems.sort((a, b) => {
            if (a.vatScheme < b.vatScheme) return -1;
            if (a.vatScheme > b.vatScheme) return 1;
            return 0;
        });
    }
}

export async function saveAssistOrAutoVat(
    endDate: string,
    companyId: string,
    accountingPeriodId: string,
    declarationId: string,
    cerfaNumber: string,
    startDate: string,
    assistVatItems: VatItemAssist[],
    autoVatItems: VatItemAuto[],
    previousVatCredit: PreviousVatCredit,
    assistVatMode: boolean,
    autoVatMode: boolean,
): Promise<string> {
    // year
    let year: string = "2024";
    if (endDate) {
        const endDateYear: string = moment(endDate).format("YYYY");
        if (+endDateYear > 2024) {
            year = endDateYear;
        }
    }
    //
    const declaration = await DeclarationFront.instanciate(
        companyId,
        accountingPeriodId,
        declarationId,
        cerfaNumber,
        year,
        startDate,
        endDate,
    );
    const decId = await declaration.saveOrDuplicateDeclaration(
        declarationId || "",
        assistVatItems,
        autoVatItems,
        assistVatMode,
        autoVatMode,
        previousVatCredit,
        null,
    );
    return decId;
}

export function storeVatItems(
    autoVatItems: VatItemAuto[],
    assistVatItems: VatItemAssist[],
    previousVatCredit: PreviousVatCredit,
    $store: any,
) {
    if (autoVatItems) {
        $store?.commit("updateAutoVatItems", autoVatItems);
    }
    if (assistVatItems) {
        $store?.commit("updateAssistVatItems", assistVatItems);
    }
    if (previousVatCredit) {
        $store?.commit("updatePreviousVatCredit", previousVatCredit);
    }
}
